<template>
  <div>
    <gat-grid
      :noPadding="true"
      :items="lines"
      footer-class="px-3  font-weight-bold"
      footer-style="font-size:16px"
      :columns="columns"
      class="elevation-0"
      dense>
      <template #cell="{ column, row, value }">
        <!-- Description Column -->
        <template v-if="column.field == 'PRICE_TEXT'">
          <gat-edit
            v-bind="getSharedInputProps(column.header)"
            :value="row.PRICE_TEXT"
            @blur="
              [
                $emit('line-updated', { line: { ...row, PRICE_TEXT: $event.target.value }, field: 'PRICE_TEXT' }),
                handleOnInpuFocused(column.header, false),
              ]
            "
            @focus="handleOnInpuFocused(column.header, true)"
            :max-chars="applicationStore.getColumnLength('Proforma_Item.PRICE_TEXT')" />
        </template>
        <!-- QTY Column -->
        <template v-else-if="column.field == 'QUANTITY_IN'">
          <gat-edit
            v-if="!row.USE_QTY_CHECKBOX"
            v-bind="getSharedInputProps(column.header)"
            :value="row.QUANTITY_IN"
            data-type="number"
            reverse
            :decimals="2"
            @blur="[onQuantityChange(row, $event.target.value), , handleOnInpuFocused(column.header, false)]"
            @focus="handleOnInpuFocused(column.header, true)" />

          <div class="d-flex justify-center" v-else>
            <v-simple-checkbox
              :value="row.QUANTITY_IN === 1 ? true : false"
              :indeterminate="row.QUANTITY_IN > 1"
              :disabled="readOnly"
              color="primary"
              @input="onQuantityChange(row, $event === true ? '1' : '0')">
            </v-simple-checkbox>
          </div>
        </template>
        <!-- Unit column -->
        <template v-else-if="column.field == 'QUANTITY_LABEL'">
          <gat-edit
            v-bind="getSharedInputProps(column.header)"
            :value="row.QUANTITY_LABEL"
            @blur="
              [
                $emit('line-updated', {
                  line: { ...row, QUANTITY_LABEL: $event.target.value },
                  field: 'QUANTITY_LABEL',
                }),
                handleOnInpuFocused(column.header, false),
              ]
            "
            @focus="handleOnInpuFocused(column.header, true)"
            :max-chars="applicationStore.getColumnLength('Proforma_Item.QUANTITY_LABEL')" />
        </template>
        <!-- Price column -->
        <gat-edit
          v-else-if="column.field == 'RATE'"
          :value="row.RATE"
          v-bind="getSharedInputProps(column.header)"
          data-type="number"
          :decimals="2"
          @blur="[onRateChange(row, $event.target.value), handleOnInpuFocused(column.header, false)]"
          @focus="handleOnInpuFocused(column.header, true)"
          reverse />
        <!-- Currency column -->
        <template v-else-if="column.field == 'CURRENCY_CODE'">
          <gat-edit
            v-bind="getSharedInputProps(column.header, true)"
            :value="row.CURRENCY_CODE"
            reverse
            :max-chars="0" />
        </template>
        <!-- Multiplier column -->
        <template v-else-if="column.field == 'MULTIPLE'">
          <div class="d-flex">
            <gat-edit
              v-bind="getSharedInputProps(column.header)"
              :value="row.MULTIPLE"
              data-type="number"
              @blur="
                ($event) => [onMultiplierChange(row, $event.target.value), handleOnInpuFocused(column.header, false)]
              "
              @focus="handleOnInpuFocused(column.header, true)">
              <template #append v-if="row.MULTIPLIER_ITEMS.length > 0">
                <div
                  style="cursor: pointer"
                  class="d-flex flex-grow-1"
                  @click="onMultiplierClick(row.MULTIPLIER_ITEMS, row.ID)">
                  <v-icon dense>mdi-dots-horizontal</v-icon>
                </div>
              </template>
            </gat-edit>
          </div>
        </template>
        <!-- Sub total Column -->
        <gat-edit
          v-else-if="column.field == 'SubTotal'"
          v-bind="getSharedInputProps(column.header, true)"
          :value="
            getSubTotalValue(row.QUANTITY_IN, row.RATE, row.MULTIPLE) === 0
              ? undefined
              : getSubTotalValue(row.QUANTITY_IN, row.RATE, row.MULTIPLE)
          "
          :decimals="2"
          data-type="number"
          reverse />

        <!-- Rebate Percent column "+/-%" -->
        <template v-else-if="column.field == 'REBATE_IN'">
          <gat-edit
            v-bind="getSharedInputProps(column.header)"
            :value="row.REBATE_IN"
            :decimals="2"
            prefix="%"
            data-type="number"
            data-form-type="number"
            @blur="[onRebatePercentChange(row, $event.target.value), handleOnInpuFocused(column.header, false)]"
            @focus="handleOnInpuFocused(column.header, true)"
            reverse />
        </template>
        <!-- Rebate Amount column "+/- sum" -->
        <template v-else-if="column.field == 'REBATE_AMOUNT'">
          <gat-edit
            v-bind="getSharedInputProps(column.header)"
            :value="row.REBATE_AMOUNT"
            data-type="number"
            reverse
            :decimals="2"
            @blur="[onRebateAmountChange($event.target.value, row), handleOnInpuFocused(column.header, false)]"
            @focus="handleOnInpuFocused(column.header, true)" />
        </template>

        <template v-else-if="column.field == 'VAT_PERCENT'">
          <gat-edit
            v-bind="getSharedInputProps(column.header, false)"
            reverse
            prefix="%"
            :decimals="2"
            :hide-details="true"
            data-type="number"
            :value="row.VAT_PERCENT"
            @blur="[onVatPercentChange(row, $event.target.value), handleOnInpuFocused(column.header, false)]"
            @focus="handleOnInpuFocused(column.header, true)" />
        </template>
        <template v-else-if="column.field == 'VAT'">
          <gat-edit
            v-bind="getSharedInputProps(column.header, true)"
            :value="row.VAT === 0 ? undefined : row.VAT"
            :decimals="2"
            reverse
            data-type="number" />
        </template>
        <!-- Total Column -->
        <template v-else-if="column.field == 'AMOUNT_IN'">
          <gat-edit
            v-bind="getSharedInputProps(column.header, true)"
            :value="row.AMOUNT_IN"
            :decimals="2"
            reverse
            data-type="number" />
        </template>
        <template v-else-if="column.field == 'COMMENTS'">
          <gat-edit
            v-bind="getSharedInputProps(column.header)"
            :value="row.COMMENTS"
            @blur="
              ($event) => [
                $emit('line-updated', { line: { ...row, COMMENTS: $event.target.value }, field: 'COMMENTS' }),
                handleOnInpuFocused(column.header, false),
              ]
            "
            @focus="handleOnInpuFocused(column.header, true)"
            :max-chars="applicationStore.getColumnLength('Proforma_Item.COMMENTS')" />
        </template>
        <template v-else-if="column.field == 'TARIFF_DESCRIPTION'">
          <gat-edit
            v-bind="getSharedInputProps(column.header)"
            :value="row.TARIFF_DESCRIPTION"
            @blur="
              ($event) => [
                $emit('line-updated', {
                  line: { ...row, TARIFF_DESCRIPTION: $event.target.value },
                  field: 'TARIFF_DESCRIPTION',
                }),
                handleOnInpuFocused(column.header, false),
              ]
            "
            @focus="handleOnInpuFocused(column.header, true)"
            :max-chars="0" />
        </template>
        <template v-else-if="column.field === 'CUR1'">
          <gat-edit
            v-bind="getSharedInputProps(column.header, true)"
            :value="row.CUR1"
            data-type="number"
            :decimals="2"
            reverse />
        </template>
        <template v-else-if="column.field === 'CUR2'">
          <gat-edit
            v-bind="getSharedInputProps(column.header, true)"
            :value="row.CUR2"
            data-type="number"
            :decimals="2"
            reverse />
        </template>
        <template v-else-if="column.field === 'CUR3'">
          <gat-edit
            v-bind="getSharedInputProps(column.header, true)"
            :value="row.CUR3"
            data-type="number"
            :decimals="2"
            reverse />
        </template>
        <template v-else-if="column.field === 'CUR4'">
          <gat-edit
            v-bind="getSharedInputProps(column.header, true)"
            :value="row.CUR4"
            data-type="number"
            :decimals="2"
            reverse />
        </template>

        <div v-else>
          {{ value }}
        </div>
      </template>
    </gat-grid>
    <gat-dialog
      title="Help lines"
      v-model="multiplierDialog.showDialog"
      @okClick="onDialogOkClick"
      ok-button-caption="ok">
      <gat-grid
        keyName="ID"
        :items="multiplierDialog.items"
        :columns="multipliercolumns"
        selectable
        :selected-keys="multiplierDialog.selectedMultiplierIds"
        @selectionChanged="($event) => (multiplierDialog.selectedMultiplierIds = $event)"
        @row-clicked="onMultiplierRowClick">
      </gat-grid>
    </gat-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { useApplicationStore } from '@/store/applicationStore';
import type { ColumnDef } from '@/types/ColumnDef';
import type { ProformaMultiplierItem, ProformaLine, ProformaCurrency, ProformaInfo } from '@/types/ProformaTypes';
import { getSubTotalValue } from '../utils/proforma-utils';

export default defineComponent({
  props: {
    lines: { type: Array as PropType<ProformaLine[]>, required: true },
    currency: { type: Array as PropType<ProformaCurrency[]>, required: true },
    proformaInfo: { type: Object as PropType<ProformaInfo>, required: true },
    LOCAL_CURRENCY: { type: String },
    readOnly: { type: Boolean, default: true },
  },
  emits: ['line-updated'],
  setup() {
    const applicationStore = useApplicationStore();
    return { applicationStore, getSubTotalValue };
  },
  data() {
    return {
      multiplierDialog: {
        items: [] as ProformaMultiplierItem[],
        showDialog: false,
        selectedMultiplierIds: [] as number[],
        proformaLineID: 0 as number,
      },
      focusedInputs: [] as string[],
      columnSize: '70px' as string,
    };
  },
  methods: {
    handleOnInpuFocused(label: string, isFocused: boolean = true) {
      const idx = this.focusedInputs.findIndex((x) => x === label);
      if (isFocused && idx === -1) {
        this.focusedInputs.push(label);
      }
      if (!isFocused) {
        this.focusedInputs = this.focusedInputs.filter((x) => x !== label);
      }
    },
    onMultiplierClick(multiplierItems: ProformaMultiplierItem[], proformaLineID: number) {
      this.multiplierDialog.items = multiplierItems;
      this.multiplierDialog.showDialog = true;
      this.multiplierDialog.proformaLineID = proformaLineID;
      this.multiplierDialog.selectedMultiplierIds = [];
    },
    onMultiplierRowClick(multiplierItem: ProformaMultiplierItem) {
      const idx = this.multiplierDialog.selectedMultiplierIds.findIndex((i) => i == multiplierItem.ID);
      if (idx >= 0) {
        this.multiplierDialog.selectedMultiplierIds.splice(idx, 1);
      } else {
        this.multiplierDialog.selectedMultiplierIds.push(multiplierItem.ID);
      }
    },
    onDialogOkClick() {
      let multiplierSum = 0;
      this.multiplierDialog.selectedMultiplierIds.forEach((multiplierID) => {
        const value = this.multiplierDialog.items.find((x) => x.ID === multiplierID)?.VALUE ?? 0;
        multiplierSum += value;
      });
      const proformaLine = this.lines.find((x) => x.ID === this.multiplierDialog.proformaLineID);
      if (proformaLine) {
        const newProformaLine: ProformaLine = { ...proformaLine, MULTIPLE: multiplierSum };
        this.$emit('line-updated', {
          line: newProformaLine,
          field: 'MULTIPLE',
        });
      }
    },

    onQuantityChange(proformaLine: ProformaLine, inputValue: string) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      const newProformaLine: ProformaLine = { ...proformaLine, QUANTITY_IN: value };

      this.$emit('line-updated', {
        line: newProformaLine,
        field: 'QUANTITY_IN',
      });
    },
    onRateChange(proformaLine: ProformaLine, inputValue: string) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      const newProformaLine: ProformaLine = { ...proformaLine, RATE: value };
      this.$emit('line-updated', {
        line: newProformaLine,
        field: 'RATE',
      });
    },
    onMultiplierChange(proformaLine: ProformaLine, inputValue: string) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      const newProformaLine: ProformaLine = { ...proformaLine, MULTIPLE: value };

      this.$emit('line-updated', {
        line: newProformaLine,
        field: 'MULTIPLE',
      });
    },

    onRebatePercentChange(proformaLine: ProformaLine, inputValue: string) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      if (value) {
        const newProformaLine: ProformaLine = { ...proformaLine, REBATE_IN: value, REBATE_AMOUNT: undefined };

        this.$emit('line-updated', {
          line: newProformaLine as ProformaLine,
          field: 'REBATE_IN',
        });
      }
    },
    onRebateAmountChange(inputValue: string, proformaLine: ProformaLine) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      console.log(value, proformaLine.REBATE_AMOUNT);
      if (value !== proformaLine.REBATE_AMOUNT) {
        const newProformaLine: ProformaLine = { ...proformaLine, REBATE_AMOUNT: value, REBATE_IN: undefined };

        this.$emit('line-updated', {
          line: newProformaLine,
          field: 'REBATE_AMOUNT',
        });
      }
    },
    onVatPercentChange(proformaLine: ProformaLine, inputValue: string) {
      let value: number | undefined = parseFloat(inputValue.replace(',', '.'));
      if (Number.isNaN(value) || value === 0) {
        value = undefined;
      }
      const newProformaLine = { ...proformaLine, VAT_PERCENT: value };
      this.$emit('line-updated', {
        line: newProformaLine,
        field: 'VAT_PERCENT',
      });
    },

    getSharedInputProps(label: string, showAsReadonly: boolean = false) {
      const readonly = this.readOnly;
      let backgroundColor: string | undefined;
      if (showAsReadonly) {
        backgroundColor = this.$vuetify.theme.dark ? 'grey darken-3' : 'grey lighten-4';
      }
      const hideDetails = this.focusedInputs.includes(label) ? true : 'auto';
      const noflex = true;
      const dense = true;
      const disabled = showAsReadonly;
      return {
        readonly,
        backgroundColor,
        hideDetails,
        noflex,
        dense,
        solo: true,
        flat: true,
        outlined: false,
        disabled,
      };
    },
  },
  computed: {
    columns() {
      const result: ColumnDef<ProformaLine & { SubTotal: string }>[] = [
        { header: 'Description', width: '225px', field: 'PRICE_TEXT', align: 'start' },
        {
          header: 'QTY',
          width: '80px',
          field: 'QUANTITY_IN',
          dataType: 'currency',
          decimals: 2,
          align: 'start',
        },
        { header: 'Unit', width: '120px', field: 'QUANTITY_LABEL', align: 'start' },
        {
          header: 'Price',
          width: '130px',
          field: 'RATE',
          dataType: 'currency',
          decimals: 2,
          align: 'start',
        },
        { header: 'Currency', width: '60px', field: 'CURRENCY_CODE', align: 'start' },
        {
          header: 'Multiplier',
          width: '90px',
          field: 'MULTIPLE',
        },
        {
          header: 'Sub total',
          width: '130px',
          field: 'SubTotal',
          align: 'start',
          dataType: 'currency',
          decimals: 2,
        },
        {
          header: '+/- %',
          width: '100px',
          align: 'start',
          dataType: 'currency',
          field: 'REBATE_IN',
          decimals: 2,
        },
        {
          header: '+/- sum',
          width: '130px',
          dataType: 'currency',
          align: 'start',
          field: 'REBATE_AMOUNT',
          decimals: 2,
        },
        {
          header: 'VAT %',
          width: '100px',
          field: 'VAT_PERCENT',
        },
        {
          header: 'VAT',
          width: '120px',
          dataType: 'currency',
          field: 'VAT',
          align: 'start',
          decimals: 2,
        },
        {
          header: `Total ${this.LOCAL_CURRENCY}`,
          dataType: 'currency',
          width: '140px',
          field: 'AMOUNT_IN',
          align: 'start',
          decimals: 2,
          footerFunction: { fn: 'sum' },
        },
      ];
      if (this.currency) {
        this.currency
          .filter((i) => i.PER_SHOW === 1)
          .forEach((element, index) => {
            const field = `CUR${index + 1}`;
            if (field === 'CUR1' || field === 'CUR2' || field === 'CUR3' || field === 'CUR4') {
              result.push({
                header: `Total ${element.CURRENCY_NAME}`,
                field,
                dataType: 'currency',
                width: '140px',
                align: 'start',
                decimals: 2,
                footerFunction: { fn: 'sum' },
              });
            }
          });
      }
      result.push(
        { header: 'Comments', width: '150px', field: 'COMMENTS', dataType: 'string' },
        { header: 'Tariff description', width: '150px', nowrap: true, field: 'TARIFF_DESCRIPTION', dataType: 'string' }
      );
      // Set default header properties.
      return result.map((x: any) => ({ ...x, unSortable: true }));
    },
    multipliercolumns() {
      const result: any = [
        {
          header: 'Description',
          field: 'TEXT',
        },
        {
          header: 'Multiplier',
          field: 'VALUE',
        },
      ];

      return result;
    },
  },
});
</script>

<style scoped lang="scss">
::v-deep
  .v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:not(:last-child)
  > td:not(.v-data-table__mobile-row),
.theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:not(:last-child)
  > th:not(.v-data-table__mobile-row) {
  border: thin solid #e0e0e0 !important;
}

::v-deep
  .theme--light.v-data-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
  background: none !important;
}

// Set the text color of disabled inputs.
::v-deep .theme--light.v-input--is-disabled > .v-input__control > .v-input__slot > .v-text-field__slot > input {
  color: rgba(0, 0, 0, 0.7) !important;
}

::v-deep .theme--dark.v-input--is-disabled > .v-input__control > .v-input__slot > .v-text-field__slot > input {
  color: rgba(255, 255, 255, 0.7) !important;
}

// Remove ltr margin-right from the checkbox.
::v-deep .v-simple-checkbox > .v-input--selection-controls__input {
  margin-right: 0px !important;
}
</style>
